/**
 * AdvanceMaturityAction — the Context **advance maturity** primary action
 * (frontend-entity-coverage §2 / §6: `PATCH maturity_stage`, the 10-stage
 * lifecycle `ambient_field → … → infrastructure`, canonical `03 §2`).
 *
 * The 10-stage ladder is the load-bearing Context UX ("not a bureaucratic form,
 * but a maturity badge"). This control surfaces the *next* stage and its human
 * "next step" hint (from {@link maturityNextStep}), and on click issues a
 * `PATCH /api/contexts/:id { maturity_stage }` through the shared
 * {@link useApiAction} (boxed worker session + cache invalidation).
 *
 * The `maturity_stage` PATCH is `[PROPOSED]` (coverage §6); the worker may not
 * accept it yet. The action therefore **degrades, never crashes**: a failed
 * PATCH only raises the standard error toast (via `useApiAction`) and leaves the
 * record untouched — the page never hard-errors. At the terminal
 * `infrastructure` stage the button is replaced by a calm "fully matured" note.
 */
import { ArrowUpRight } from "@/components/ui/icon";

import { CONTEXT_MATURITY_STAGES, type ContextMaturityStage } from "@aos/shared";
import { humanize } from "@/lib/format";
import { Button } from "@/components/ui/button";

import { useApiAction } from "../_shared/useApiAction";
import { maturityNextStep } from "./MaturityBadge";

/** The next maturity stage in the ladder, or `null` at the terminal stage. */
export function nextMaturityStage(
  stage: ContextMaturityStage,
): ContextMaturityStage | null {
  const idx = CONTEXT_MATURITY_STAGES.indexOf(stage);
  if (idx < 0 || idx >= CONTEXT_MATURITY_STAGES.length - 1) return null;
  return CONTEXT_MATURITY_STAGES[idx + 1] ?? null;
}

export interface AdvanceMaturityActionProps {
  contextId: string;
  stage: ContextMaturityStage;
  /** Render as the single filled primary button (Header) vs. an outline button. */
  variant?: "primary" | "outline";
  /** Called after a successful advance so the host can refetch. */
  onAdvanced?: () => void;
}

/**
 * Button that advances a context one maturity stage. Shows the target stage
 * label; tooltip carries the "next step" hint. Terminal stage → muted note.
 */
export function AdvanceMaturityAction({
  contextId,
  stage,
  variant = "primary",
  onAdvanced,
}: AdvanceMaturityActionProps) {
  const { run, loading } = useApiAction();
  const next = nextMaturityStage(stage);

  if (!next) {
    return (
      <span className="text-sm text-muted-foreground">
        Fully matured (infrastructure).
      </span>
    );
  }

  const advance = async () => {
    try {
      await run(`/contexts/${encodeURIComponent(contextId)}`, {
        method: "PATCH",
        body: { maturity_stage: next },
        invalidateResources: ["contexts"],
        successMessage: `Context advanced to ${humanize(next)}.`,
      });
      onAdvanced?.();
    } catch {
      // useApiAction already surfaced the error toast; degrade silently.
    }
  };

  return (
    <Button
      variant={variant === "primary" ? "default" : "outline"}
      disabled={loading}
      onClick={() => void advance()}
      title={`Next step: ${maturityNextStep(stage)}`}
      data-testid="context-advance-maturity"
    >
      <ArrowUpRight className="mr-2 h-4 w-4" />
      Advance to {humanize(next)}
    </Button>
  );
}

export default AdvanceMaturityAction;
